import React, { useState } from 'react';
import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import { ChevronDownIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../store/useAuthStore';
import { useAuthUser } from '../../features/auth/api/auth';
import { DASHBOARD_URL } from '../../config/apiConfig';

const platformMenu = {
  generateLeads: [
    { label: 'People & Company Search', sub: 'Find prospects that match your ICP', icon: '🔍' },
    { label: 'Chrome Extension', sub: 'Build lists and prospect from anywhere', icon: '🧩' },
    { label: 'CRM Data Enrichment', sub: 'Turn any datapoint into a full profile', icon: '🔄' },
  ],
  enrichData: [
    { label: 'Find work emails', sub: 'Find verified work emails instantly', icon: '✉️' },
    { label: 'Find mobile numbers', sub: 'Reach prospects via direct mobile numbers', icon: '📱' },
    { label: 'Find emails from domains', sub: 'Reveal key contacts at any company', icon: '🌐' },
    { label: 'Intent Signals', sub: 'Spot companies that are ready to buy', icon: '⚡' },
  ],
  buildAutomate: [
    { label: 'API', sub: 'Find and enrich leads at scale', icon: '🔗' },
    { label: 'Integrations', sub: 'Connect Director Discovery to your stack in minutes', icon: '🔌' },
  ],
};

const whyMenu = {
  useCases: [
    { label: 'For Sales Teams', sub: 'More meetings, less waste' },
    { label: 'For RevOps', sub: 'Build predictable pipelines' },
    { label: 'For Founders', sub: 'Find your first customers' },
    { label: 'For Outbound Agencies', sub: 'Cleaner data, better campaigns' },
  ],
  alternatives: [
    'Director Discovery vs ZoomInfo',
    'Director Discovery vs Apollo',
    'Director Discovery vs Cognism',
    'Director Discovery vs Lusha',
  ],
};

const resourcesMenu = [
  { label: 'Our APIs', sub: 'Search, enrich, and sync data' },
  { label: 'API Documentation', sub: 'Explore technical docs and examples' },
  { label: 'Helpdesk', sub: 'Find quick answers and tips' },
  { label: 'Referral Program', sub: 'Earn 20% commission for 12 months' },
  { label: 'Sales Hub', sub: 'Sales & prospecting tips for pros' },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hoveredMenu, setHoveredMenu] = useState<string | null>(null);
  
  useAuthUser();
  const { isAuthenticated } = useAuthStore();

  let timeoutId: NodeJS.Timeout;

  const handleMouseEnter = (menuName: string) => {
    clearTimeout(timeoutId);
    setHoveredMenu(menuName);
  };

  const handleMouseLeave = () => {
    timeoutId = setTimeout(() => {
      setHoveredMenu(null);
    }, 150);
  };

  return (
    <nav className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-2 flex-shrink-0">
            <div className="w-7 h-7 bg-indigo-600 rounded-md flex items-center justify-center">
              <span className="text-white text-xs font-bold">P</span>
            </div>
            <span className="text-gray-900 font-bold text-lg tracking-tight">Director Discovery</span>
          </div>

          <div className="hidden lg:flex items-center gap-1">
            <div 
              className="relative"
              onMouseEnter={() => handleMouseEnter('platform')}
              onMouseLeave={handleMouseLeave}
            >
              <button className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-indigo-600 rounded-md hover:bg-gray-50 transition-colors outline-none cursor-pointer">
                Platform <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${hoveredMenu === 'platform' ? 'rotate-180 text-indigo-600' : ''}`} />
              </button>
              <Transition
                show={hoveredMenu === 'platform'}
                as={Fragment}
                enter="transition ease-out duration-150"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <div className="absolute left-0 mt-2 w-[700px] bg-white rounded-2xl shadow-xl border border-gray-100 p-6 z-50">
                  <div className="grid grid-cols-3 gap-6">
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Generate Leads</p>
                      {platformMenu.generateLeads.map((item) => (
                        <a key={item.label} href="#" className="flex items-start gap-3 p-2 rounded-xl hover:bg-indigo-50 group transition-colors mb-1">
                          <span className="text-lg mt-0.5">{item.icon}</span>
                          <div>
                            <p className="text-sm font-medium text-gray-800 group-hover:text-indigo-600">{item.label}</p>
                            <p className="text-xs text-gray-500">{item.sub}</p>
                          </div>
                        </a>
                      ))}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Enrich Data</p>
                      {platformMenu.enrichData.map((item) => (
                        <a key={item.label} href="#" className="flex items-start gap-3 p-2 rounded-xl hover:bg-indigo-50 group transition-colors mb-1">
                          <span className="text-lg mt-0.5">{item.icon}</span>
                          <div>
                            <p className="text-sm font-medium text-gray-800 group-hover:text-indigo-600">{item.label}</p>
                            <p className="text-xs text-gray-500">{item.sub}</p>
                          </div>
                        </a>
                      ))}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Build & Automate</p>
                      {platformMenu.buildAutomate.map((item) => (
                        <a key={item.label} href="#" className="flex items-start gap-3 p-2 rounded-xl hover:bg-indigo-50 group transition-colors mb-1">
                          <span className="text-lg mt-0.5">{item.icon}</span>
                          <div>
                            <p className="text-sm font-medium text-gray-800 group-hover:text-indigo-600">{item.label}</p>
                            <p className="text-xs text-gray-500">{item.sub}</p>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              </Transition>
            </div>

            <a href="#" className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-indigo-600 rounded-md hover:bg-gray-50 transition-colors">Our data</a>

            <div 
              className="relative"
              onMouseEnter={() => handleMouseEnter('why')}
              onMouseLeave={handleMouseLeave}
            >
              <button className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-indigo-600 rounded-md hover:bg-gray-50 transition-colors outline-none cursor-pointer">
                Why Director Discovery <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${hoveredMenu === 'why' ? 'rotate-180 text-indigo-600' : ''}`} />
              </button>
              <Transition
                show={hoveredMenu === 'why'}
                as={Fragment}
                enter="transition ease-out duration-150"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <div className="absolute left-0 mt-2 w-[520px] bg-white rounded-2xl shadow-xl border border-gray-100 p-6 z-50">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Use cases</p>
                      {whyMenu.useCases.map((item) => (
                        <a key={item.label} href="#" className="block p-2 rounded-xl hover:bg-indigo-50 group transition-colors mb-1">
                          <p className="text-sm font-medium text-gray-800 group-hover:text-indigo-600">{item.label}</p>
                          <p className="text-xs text-gray-500">{item.sub}</p>
                        </a>
                      ))}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Alternatives</p>
                      {whyMenu.alternatives.map((item) => (
                        <a key={item} href="#" className="block p-2 rounded-xl hover:bg-indigo-50 text-sm font-medium text-gray-700 hover:text-indigo-600 transition-colors mb-1">{item}</a>
                      ))}
                      <a href="#" className="text-xs text-indigo-600 font-medium hover:underline mt-2 block">View all alternatives →</a>
                    </div>
                  </div>
                </div>
              </Transition>
            </div>

            <div 
              className="relative"
              onMouseEnter={() => handleMouseEnter('resources')}
              onMouseLeave={handleMouseLeave}
            >
              <button className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-700 hover:text-indigo-600 rounded-md hover:bg-gray-50 transition-colors outline-none cursor-pointer">
                Resources <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${hoveredMenu === 'resources' ? 'rotate-180 text-indigo-600' : ''}`} />
              </button>
              <Transition
                show={hoveredMenu === 'resources'}
                as={Fragment}
                enter="transition ease-out duration-150"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <div className="absolute left-0 mt-2 w-72 bg-white rounded-2xl shadow-xl border border-gray-100 p-4 z-50">
                  {resourcesMenu.map((item) => (
                    <a key={item.label} href="#" className="block p-2 rounded-xl hover:bg-indigo-50 group transition-colors mb-1">
                      <p className="text-sm font-medium text-gray-800 group-hover:text-indigo-600">{item.label}</p>
                      <p className="text-xs text-gray-500">{item.sub}</p>
                    </a>
                  ))}
                </div>
              </Transition>
            </div>

            <a href="#" className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-indigo-600 rounded-md hover:bg-gray-50 transition-colors">Pricing</a>
          </div>

          <div className="hidden lg:flex items-center gap-3">
            {isAuthenticated ? (
              <Link to={DASHBOARD_URL}className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors shadow-sm">
                Go to Dashboard
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-sm font-medium text-gray-700 hover:text-indigo-600 transition-colors">Log in</Link>
                <Link to="/register" className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors shadow-sm">
                  Get started for free
                </Link>
              </>
            )}
          </div>

          <button className="lg:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100" onClick={() => setMobileOpen(!mobileOpen)}>
            {mobileOpen ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-gray-100 bg-white px-4 py-4 space-y-2 shadow-lg absolute w-full">
          {['Platform', 'Our data', 'Why Director Discovery', 'Resources', 'Pricing'].map((item) => (
            <a key={item} href="#" className="block py-2 text-sm font-medium text-gray-700 hover:text-indigo-600">{item}</a>
          ))}
          <div className="pt-4 flex flex-col gap-2">
            {isAuthenticated ? (
               <Link to={DASHBOARD_URL} className="bg-indigo-600 text-white text-sm font-semibold text-center px-4 py-2.5 rounded-lg hover:bg-indigo-700">
                 Go to Dashboard
               </Link>
            ) : (
               <>
                 <Link to="/login" className="text-sm font-medium text-gray-700 text-center py-2 border border-gray-200 rounded-lg hover:bg-gray-50">Log in</Link>
                 <Link to="/register" className="bg-indigo-600 text-white text-sm font-semibold text-center px-4 py-2.5 rounded-lg hover:bg-indigo-700">Get started for free</Link>
               </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}